import { message } from 'antd';
import { useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { validateBeneficiaryAccountName } from '@monorepo/client-common';
import {
  AirwallexForm,
  PayoutPayload,
  WithdrawPayload,
} from '@monorepo/client-types';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  AccountStatus,
  PaymentMethod,
  PayoutSource,
  WithdrawRulesResponse,
} from '@monorepo/types';
import { MAX_CRYPTO_WITHDRAWAL_LIMIT } from '@monorepo/types';
import { ElementTypes } from '@airwallex/components-sdk';
import { useDashboardContainer } from '@hub/features/dashboard-templates/dashboard-container';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';

interface UsePayoutSubmissionProps {
  source: PayoutSource;
  sourceId: string;
  maxWithdrawal: number;
  showAmountSelection?: boolean;
  validateWithDrawEligible: () => Promise<WithdrawRulesResponse>;
  modalRef: React.RefObject<any | null>;
  form: UseFormReturn<WithdrawPayload>;
}

export const usePayoutSubmission = ({
  source,
  sourceId,
  maxWithdrawal,
  showAmountSelection,
  validateWithDrawEligible,
  modalRef,
  form,
}: UsePayoutSubmissionProps) => {
  const { updateAccountStatus } = useDashboardContainer();
  const enabledWithdrawMechanism = useGetFeatureFlag(
    'enable-revert-withdrawal-mechanism',
    false,
    {
      env: EnvService.application,
    },
  );

  const { isLoading, updateLoadingState } = useLoading();
  const { t } = useTranslation(['withdrawFunds', 'errors']);

  const {
    dataStore: { payoutStore },
  } = useStore();

  const beneficiaryFormRef = useRef<
    ElementTypes['beneficiaryForm'] | undefined
  >(undefined);

  const handleAirwallexWithdraw = async (payoutPayload: PayoutPayload) => {
    if (!beneficiaryFormRef.current) {
      message.error(t('errors:airwallex_withdraw_error'), 3);
      throw new Error('Airwallex form not found');
    }
    try {
      const airwallexForm =
        (await beneficiaryFormRef.current.submit()) as unknown as AirwallexForm;

      if (airwallexForm?.errors) {
        message.error(t('errors:error_submitting_form'), 3);
        throw new Error('Error submitting form');
      }

      try {
        validateBeneficiaryAccountName(airwallexForm.values);
      } catch (e) {
        message.error(t('errors:account_name_digits_only'));
        throw e;
      }

      //FIXME[epic=cart] fix type
      const beneficiary = (await payoutStore.createAirwallexBeneficiary(
        airwallexForm.values,
      )) as any;

      if (!beneficiary) {
        message.error(t('errors:create_payout_error'));
        throw new Error('Error creating beneficiary');
      }

      payoutPayload.metadata = {
        ...payoutPayload.metadata,
        beneficiaryId: beneficiary.id,
      };

      return beneficiary;
    } catch (e) {
      updateLoadingState(LoadingState.Error);
      throw e;
    }
  };

  const createPayoutRequest = async (payoutPayload: PayoutPayload) => {
    try {
      const response = await payoutStore.createPayoutRequest({
        ...payoutPayload,
      });

      if (response?.status === 'pending') {
        message.success(t('withdrawFunds:request_submitted_successfully'));
        await validateWithDrawEligible();
        handleCancel();
      } else if (
        response.status === 400 &&
        response.message.includes(
          'Amount exceeds maximum crypto withdrawal limit of',
        )
      ) {
        message.error(
          t('errors:withdrawal_limit_dynamic', {
            limit: MAX_CRYPTO_WITHDRAWAL_LIMIT,
          }),
        );
      } else {
        message.error(
          response.message === 'Amount is higher than available amount'
            ? t('errors:error_amount')
            : t('errors:error_refresh'),
        );
      }
      updateLoadingState(LoadingState.Loaded);
    } catch (e) {
      console.error('Failed creating a payout', e);
      message.error(t('errors:create_payout_error'));
      updateLoadingState(LoadingState.Error);
    }
  };

  const handleCancel = () => {
    modalRef.current?.cancel();
  };

  const handleSubmit = async () => {
    updateLoadingState(LoadingState.Loading);

    const formValues = form.getValues();

    const isAirwallex = formValues.paymentOption === PaymentMethod.Airwallex;
    const amount = showAmountSelection
      ? form.getValues().amount
      : maxWithdrawal;

    const payoutPayload: PayoutPayload = {
      ...formValues,
      amount,
      source,
      sourceId,
    };

    try {
      if (isAirwallex) {
        try {
          await handleAirwallexWithdraw(payoutPayload);
        } catch (e) {
          console.error('Failed to create Airwallex beneficiary', e);
          updateLoadingState(LoadingState.Error);
          return;
        }
      }

      await createPayoutRequest(payoutPayload);

      if (enabledWithdrawMechanism) {
        updateAccountStatus(AccountStatus.WaitingForWithdraw);
      }
    } catch (e) {
      console.error('Failed creating a payout', e);
      message.error(t('errors:create_payout_error'));
      updateLoadingState(LoadingState.Error);
    }
  };

  return {
    beneficiaryFormRef,
    isLoading,
    handleSubmit,
    handleCancel,
  };
};
